import Phaser from 'phaser';
import { RiftScar as RiftScarData } from '../../types/rifts';
import { drawSafeZone } from '../utils/RadiusRenderer';

export class RiftScar extends Phaser.GameObjects.Container {
  private scarData: RiftScarData;
  private vortexGraphics: Phaser.GameObjects.Graphics;
  private glowSprite: Phaser.GameObjects.Sprite | null = null;
  private particles: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private interactionRadius: Phaser.GameObjects.Graphics;
  private safeZoneGraphics: Phaser.GameObjects.Graphics;
  private labelText: Phaser.GameObjects.Text;
  private isPlayerNearby: boolean = false;
  private rotationAngle: number = 0;
  private pulseTime: number = 0;

  constructor(scene: Phaser.Scene, scarData: RiftScarData) {
    super(scene, scarData.worldX, scarData.worldY);
    this.scarData = scarData;

    this.vortexGraphics = scene.add.graphics();
    this.add(this.vortexGraphics);

    this.interactionRadius = scene.add.graphics();
    this.interactionRadius.setAlpha(0);
    this.add(this.interactionRadius);

    // Safe zone indicator (3 tiles radius, using grid-based rendering like permanent locations)
    // Don't add to container - add directly to scene so it's not affected by container transforms
    this.safeZoneGraphics = scene.add.graphics();
    this.safeZoneGraphics.setDepth(5); // Below the rift scar visual
    drawSafeZone(this.safeZoneGraphics, scarData.worldX, scarData.worldY, 3, {
      fillColor: 0x8844ff,  // Purple for rift scars
      ringColor: 0x6633cc,
    });

    // Position label further away since scar is now small
    this.labelText = scene.add.text(0, 40, '', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: '"Sometype Mono", monospace',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 },
    });
    this.labelText.setOrigin(0.5);
    this.labelText.setAlpha(0);
    this.add(this.labelText);

    this.createGlowEffect();
    this.createParticleEffect();
    this.setupInteraction();

    scene.add.existing(this);
    this.setDepth(100);
  }

  private setupInteraction(): void {
    // Make the scar clickable - square hit area for square visual
    const hitArea = new Phaser.Geom.Rectangle(-25, -25, 50, 50);
    this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    this.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      console.log('[RIFT SCAR] Clicked:', this.scarData.name, 'Button:', pointer.button);
      // Only left-click triggers entry directly
      // Right-click is handled by InputHandler for context menu
      if (pointer.button === 0) {
        this.scene.events.emit('rift-scar-clicked', this.scarData);
      }
    });
  }

  private createGlowEffect(): void {
    // Glow effect disabled - keeping method for potential future use
  }

  private createParticleEffect(): void {
    try {
      if (!this.scene.textures.exists('particle')) {
        const graphics = this.scene.add.graphics();
        graphics.fillStyle(0xffffff, 1);
        graphics.fillCircle(4, 4, 4);
        graphics.generateTexture('particle', 8, 8);
        graphics.destroy();
      }

      // Subtle particles emanating from the crack
      this.particles = this.scene.add.particles(0, 0, 'particle', {
        speed: { min: 10, max: 30 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.6, end: 0 },
        lifespan: 1000,
        frequency: 200,
        blendMode: 'ADD',
        tint: this.getEffectColor(),
      });

      this.add(this.particles);
    } catch (error) {
      console.warn('Could not create particle effect:', error);
    }
  }

  private getEffectColor(): number {
    switch (this.scarData.effectType) {
      case 'vortex': return 0x4488ff;
      case 'tear': return 0x8844ff;
      case 'mist': return 0x44ff88;
      case 'crystalline': return 0x44ddff;
      default: return 0x4488ff;
    }
  }

  private drawVortex(): void {
    this.vortexGraphics.clear();

    const color = this.getEffectColor();
    const alpha = this.scarData.stability === 'sealed' ? 0.3 : 0.7;
    const pulse = Math.sin(this.pulseTime) * 0.2 + 0.8;

    // Draw a glowing crack/tear effect - vertical tear with jagged edges
    const crackLength = 30;
    const crackWidth = 3;
    
    // Main vertical crack
    this.vortexGraphics.lineStyle(crackWidth, color, alpha * pulse);
    this.vortexGraphics.beginPath();
    this.vortexGraphics.moveTo(0, -crackLength / 2);
    
    // Add jagged segments to make it look like a tear
    const segments = 6;
    for (let i = 1; i <= segments; i++) {
      const y = -crackLength / 2 + (crackLength / segments) * i;
      const xOffset = (Math.random() - 0.5) * 4;
      this.vortexGraphics.lineTo(xOffset, y);
    }
    
    this.vortexGraphics.strokePath();
    
    // Add horizontal cracks branching off
    const numBranches = 2;
    for (let i = 0; i < numBranches; i++) {
      const yPos = -crackLength / 2 + (crackLength / (numBranches + 1)) * (i + 1);
      const branchLength = 10 + Math.random() * 8;
      const direction = i % 2 === 0 ? 1 : -1;
      
      this.vortexGraphics.lineStyle(2, color, alpha * pulse * 0.7);
      this.vortexGraphics.beginPath();
      this.vortexGraphics.moveTo(0, yPos);
      this.vortexGraphics.lineTo(branchLength * direction, yPos + (Math.random() - 0.5) * 4);
      this.vortexGraphics.strokePath();
    }
    
    // Glowing core at center - small square
    this.vortexGraphics.fillStyle(color, alpha * pulse * 0.8);
    this.vortexGraphics.fillRect(-8, -8, 16, 16);

    // Show X mark for sealed scars
    if (this.scarData.stability === 'sealed') {
      this.vortexGraphics.lineStyle(2, 0xff4444, 0.6);
      this.vortexGraphics.beginPath();
      this.vortexGraphics.moveTo(-20, -20);
      this.vortexGraphics.lineTo(20, 20);
      this.vortexGraphics.moveTo(20, -20);
      this.vortexGraphics.lineTo(-20, 20);
      this.vortexGraphics.strokePath();
    }
  }

  update(time: number, delta: number): void {
    this.rotationAngle += delta * 0.001;
    this.pulseTime += delta * 0.002;
    this.drawVortex();
  }

  updateDistance(playerX: number, playerY: number): number {
    const wasNearby = this.isPlayerNearby;
    const distance = Phaser.Math.Distance.Between(this.x, this.y, playerX, playerY);
    
    // Interaction radius is 75px
    this.isPlayerNearby = distance < 75;

    if (this.isPlayerNearby && !wasNearby) {
      this.labelText.setText(this.scarData.name);
      this.scene.tweens.add({
        targets: this.labelText,
        alpha: 1,
        duration: 300,
      });
    } else if (!this.isPlayerNearby && wasNearby) {
      this.scene.tweens.add({
        targets: this.labelText,
        alpha: 0,
        duration: 300,
      });
    }

    return distance;
  }

  private showInteractionPrompt(): void {
    this.labelText.setText(this.scarData.name);
    this.scene.tweens.add({
      targets: this.labelText,
      alpha: 1,
      duration: 300,
    });

    this.scene.events.emit('rift-scar-nearby', this.scarData);
  }

  private hideInteractionPrompt(): void {
    this.scene.tweens.add({
      targets: this.interactionRadius,
      alpha: 0,
      duration: 300,
    });

    this.scene.tweens.add({
      targets: this.labelText,
      alpha: 0,
      duration: 300,
    });

    this.scene.events.emit('rift-scar-far', this.scarData);
  }

  async triggerEntry(): Promise<void> {
    this.scene.cameras.main.fadeOut(1000, 0, 0, 0);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  getScarData(): RiftScarData {
    return this.scarData;
  }

  isNearby(): boolean {
    return this.isPlayerNearby;
  }

  override destroy(fromScene?: boolean): void {
    if (this.glowSprite) {
      this.glowSprite.destroy();
    }
    if (this.particles) {
      this.particles.destroy();
    }
    if (this.safeZoneGraphics) {
      this.safeZoneGraphics.destroy();
    }
    super.destroy(fromScene);
  }
}
